import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api';

type SyncStatus = {
  role: string;
  syncEnabled: boolean;
  synced: boolean;
  lastError?: string | null;
  lastAttemptAt?: number | null;
  lastSuccessAt?: number | null;
  configVersion?: number;
  usersVersion?: number;
  peerConfigVersion?: number | null;
  peerUsersVersion?: number | null;
  secondarySyncUrl?: string | null;
};

function formatTime(ms?: number | null): string {
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return '—';
  }
}

function versionMatch(a?: number | null, b?: number | null): boolean {
  return a != null && b != null && a === b;
}

export function HaStatusTab({ active }: { active: boolean }) {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getSyncStatus();
        if (!cancelled) setStatus(data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load HA status');
          setStatus(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const id = window.setInterval(load, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [active]);

  if (loading && !status) {
    return <div className="ha-status-panel"><p>Loading HA status…</p></div>;
  }
  if (error && !status) {
    return <div className="ha-status-panel"><p className="error-text">{error}</p></div>;
  }
  if (!status) {
    return <div className="ha-status-panel"><p>No HA status available.</p></div>;
  }

  const roleLabel = status.role === 'secondary' ? 'Secondary' : 'Primary';
  const isPrimary = status.role === 'primary';
  const syncState = !isPrimary
    ? status.synced
      ? 'Standby (receive-only)'
      : 'Error receiving sync'
    : !status.syncEnabled
      ? 'Standalone (no secondary configured)'
      : status.synced
        ? 'Synced'
        : 'Not synced';

  const primaryConfig = isPrimary ? status.configVersion : status.peerConfigVersion;
  const secondaryConfig = isPrimary ? status.peerConfigVersion : status.configVersion;
  const primaryUsers = isPrimary ? status.usersVersion : status.peerUsersVersion;
  const secondaryUsers = isPrimary ? status.peerUsersVersion : status.usersVersion;
  const showPeerCols = isPrimary && status.syncEnabled;

  return (
    <div className="ha-status-panel">
      <div className="ha-status-header">
        <h2>HA Status</h2>
        <button
          type="button"
          className="button secondary"
          onClick={async () => {
            setLoading(true);
            try {
              setStatus(await apiService.getSyncStatus());
              setError(null);
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Refresh failed');
            } finally {
              setLoading(false);
            }
          }}
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <dl className="ha-status-meta">
        <div>
          <dt>Role</dt>
          <dd>{roleLabel}</dd>
        </div>
        <div>
          <dt>Sync state</dt>
          <dd className={status.synced || !status.syncEnabled ? 'ha-ok' : 'ha-fail'}>{syncState}</dd>
        </div>
        {isPrimary && (
          <div>
            <dt>Secondary URL</dt>
            <dd>{status.secondarySyncUrl || '—'}</dd>
          </div>
        )}
      </dl>

      {showPeerCols ? (
        <table className="ha-version-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Primary</th>
              <th>Secondary</th>
              <th>Match</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>configVersion</td>
              <td>{primaryConfig ?? '—'}</td>
              <td>{secondaryConfig ?? '—'}</td>
              <td className={versionMatch(primaryConfig, secondaryConfig) ? 'ha-ok' : 'ha-fail'}>
                {secondaryConfig == null ? '—' : versionMatch(primaryConfig, secondaryConfig) ? 'Yes' : 'No'}
              </td>
            </tr>
            <tr>
              <td>usersVersion</td>
              <td>{primaryUsers ?? '—'}</td>
              <td>{secondaryUsers ?? '—'}</td>
              <td className={versionMatch(primaryUsers, secondaryUsers) ? 'ha-ok' : 'ha-fail'}>
                {secondaryUsers == null ? '—' : versionMatch(primaryUsers, secondaryUsers) ? 'Yes' : 'No'}
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <table className="ha-version-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>This node</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>configVersion</td>
              <td>{status.configVersion ?? '—'}</td>
            </tr>
            <tr>
              <td>usersVersion</td>
              <td>{status.usersVersion ?? '—'}</td>
            </tr>
          </tbody>
        </table>
      )}

      <dl className="ha-status-meta ha-status-meta-footer">
        <div>
          <dt>Last sync attempt</dt>
          <dd>{formatTime(status.lastAttemptAt)}</dd>
        </div>
        <div>
          <dt>Last sync success</dt>
          <dd>{formatTime(status.lastSuccessAt)}</dd>
        </div>
        <div className="ha-status-wide">
          <dt>Last error</dt>
          <dd className={status.lastError ? 'ha-fail' : undefined}>
            {status.lastError || '—'}
          </dd>
        </div>
      </dl>
    </div>
  );
}
